import * as React from 'react';
import {useEffect, useState} from 'react';
import {Box, Chip, Collapse, Typography} from '@mui/material';
import type {TreeItemProps} from '@mui/x-tree-view';
import {TreeItem} from '@mui/x-tree-view';
import {useLocalization} from 'localization';
import {ColorPalette} from 'theme';
import {ChevronRightIcon} from 'components/icons/ChevronRightIcon';
import {useThesaurusSelectionContext} from '../contexts/ThesaurusSelectionContext';

type Props = TreeItemProps & {
  code: string | undefined;
  selected?: boolean;
  hasChildrenSelected?: boolean;
  onlyTopLevel?: boolean;
};

const focusedText = {fontWeight: 'bold'} as const;

const selectedChip = {
  backgroundColor: ColorPalette.secondary.beige,
  color: ColorPalette.warmGreen,
} as const;

export const ThesaurusItem: React.FC<Props> = props => {
  const {itemId, code, label, onlyTopLevel, ...rest} = props;
  const {t} = useLocalization();

  // Whether node is selected (part of the thesaurus value) or not
  const [selected, setSelected] = useState<boolean>(false);
  // Whether node is focused (clicked by user in the tree) or not
  const [focused, setFocused] = useState<boolean>(false);
  // Whether node has a child that is focused (clicked by user in the tree) or not
  const [childFocused, setChildFocused] = useState<boolean>(false);
  const {subscribe} = useThesaurusSelectionContext();

  useEffect(() => {
    return subscribe(itemId, {
      onSelectedChange: setSelected,
      onFocusedChange: setFocused,
      onChildFocusedChange: setChildFocused,
    });
  }, [itemId, subscribe]);

  return (
    <TreeItem
      itemId={itemId}
      label={
        <Box className={'labelBox'}>
          {code ? (
            <Box className={'outerBox'}>
              <Typography className={'nodeCode'} variant="subtitle1">
                {code}
              </Typography>
            </Box>
          ) : null}
          <Typography
            variant="body2"
            className={'nodeText'}
            style={focused || childFocused ? focusedText : undefined}>
            {label}
          </Typography>
          <Typography className={'nodeStatus'} variant="label">
            {selected ? (
              <Chip
                label={t('extension.thesaurusEdit.selected')}
                sx={selectedChip}
                color="primary"
                size="small"
              />
            ) : null}
            {onlyTopLevel ? <ChevronRightIcon color={'primary'} /> : null}
          </Typography>
        </Box>
      }
      {...rest}
      slots={{groupTransition: Collapse, ...rest.slots}}
    />
  );
};
